import { getSelectedUpsellOfferingsData } from "@Modules/eventPage/utils/getSelectedUpsellListingsData";
import { getIsInCartFlow } from "@Modules/eventPage/utils/cart";
import { getIsTicketingEnabled } from "@Modules/eventPage/utils/getIsTicketingEnabled";
import { useSetNumberOfCustomers } from "@Modules/eventPage/utils/useSetNumberOfCustomers";
import {
    EVENT_TYPE,
    getIfSlotsAvailable,
    pluralise,
    SELECTORS,
    useEventDataOfSelectedEvent,
} from "@Utils";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { getUpsellListingsForEventId } from "@Modules/eventPage/components/UpsellListings/utils/getUpsellListingsForEventId";
import { LISTING_TYPES } from "repoV2/constants/listing";
import { getQueryParam } from "repoV2/utils/urls&routing/urlParams";
import { URL_SEARCH_PARAMS } from "repoV2/features/Common/modules/URLsNRouting/URLsNRouting.constants";
import { ITicketCount } from "./TicketCount";

const useTicketCount = ({
    userFlow,
    disablePayment,
    dynamicLink,
    communityUuid,
    isRebooking,
}: ITicketCount.IProps) => {
    const { event, eventData } = useEventDataOfSelectedEvent();
    const {
        paymentInfo: {
            numberOfCustomers = 1,
            quantity: merchQuantity = 1,
            slots,
            otherListings: selectedUpsellOfferingsQuantities,
        },
    } = event;

    const upsellListings = getUpsellListingsForEventId({
        eventId: eventData.uuid,
        event,
    });
    const setNumberOfCustomers = useSetNumberOfCustomers();

    const hostData = useSelector(SELECTORS.hostData);
    const selectedUpsellListingsData = getSelectedUpsellOfferingsData({
        upsellListings,
        selectedUpsellOfferingsQuantities,
    });

    const isCustomerCountMoreThanOne = numberOfCustomers > 1;

    const getAreSlotsAvailable = (isNext: boolean = true): boolean =>
        !!(
            getIfSlotsAvailable({
                eventData,
                numberOfCustomers: numberOfCustomers + (isNext ? 1 : 0),
                merchQuantity,
                slots,
            }) &&
            selectedUpsellListingsData
                .filter(l => l.type === EVENT_TYPE.MERCHANDISE)
                .map(
                    l =>
                        (selectedUpsellOfferingsQuantities?.[l?.uuid]
                            ?.quantity ?? 1) *
                            (numberOfCustomers + (isNext ? 1 : 0)) <=
                        l.total_inventory - l.purchased_inventory
                )
                .every(r => r)
        );

    // Warns the user to first select a class before changing the number of users
    const invalidSelectionMessage: string | null =
        ([
            EVENT_TYPE.ONE_ON_ONE_CLASSES,
            EVENT_TYPE.GROUP_CLASSES,
            EVENT_TYPE.ROLLING_CLASSES,
        ].includes(eventData.type) &&
            slots.length === 0 &&
            "Please select a class before changing the number of users") ||
        null;

    const increaseQty = (): void => {
        let listingVerb = "";

        switch (eventData.type) {
            case EVENT_TYPE.APPOINTMENTS:
            case EVENT_TYPE.WEBINAR:
            case EVENT_TYPE.ONE_ON_ONE_CLASSES:
            case EVENT_TYPE.GROUP_CLASSES:
            case EVENT_TYPE.ROLLING_CLASSES:
            case EVENT_TYPE.WORKSHOPS:
                listingVerb = pluralise("slot", numberOfCustomers);
                break;
            case EVENT_TYPE.MERCHANDISE:
                listingVerb = pluralise("unit", numberOfCustomers);
                break;
            case EVENT_TYPE.NO_SCHEDULE:
            case EVENT_TYPE.RECORDED_CONTENT:
                listingVerb = pluralise("entry", numberOfCustomers);
                break;
            default:
                listingVerb = pluralise("unit", numberOfCustomers);
        }

        if (invalidSelectionMessage) {
            alert(invalidSelectionMessage);
            return;
        }
        if (!getAreSlotsAvailable()) {
            alert(
                `There ${
                    isCustomerCountMoreThanOne ? "are" : "is"
                } only ${numberOfCustomers} ${listingVerb} left`
            );
            return;
        }
        setNumberOfCustomers(numberOfCustomers + 1);
    };

    const decreaseQty = () => {
        if (invalidSelectionMessage) {
            alert(invalidSelectionMessage);
            return;
        }

        if (isCustomerCountMoreThanOne)
            setNumberOfCustomers(numberOfCustomers - 1);
    };

    useEffect(() => {
        if (!getAreSlotsAvailable(false) && numberOfCustomers !== 1) {
            setNumberOfCustomers(1);
        }
    }, [numberOfCustomers, slots]);

    const customEntityText: string =
        eventData?.metadata?.custom_entity_count_text || "user";

    const customPluralSuffix: string =
        eventData?.metadata?.custom_entity_plural_suffix ?? "s";

    const quantityTitle = `${numberOfCustomers} ${customEntityText}${
        isCustomerCountMoreThanOne ? customPluralSuffix : ""
    }`;

    // Flag to Restrict adding of multiple users if merchandise is present in upsell
    const hasMerchandiseInUpsell = selectedUpsellListingsData.some(
        obj => obj.type === LISTING_TYPES.MERCHANDISE
    );

    const disableDecreaseQty =
        hasMerchandiseInUpsell || !isCustomerCountMoreThanOne;
    const disableIncreaseQty =
        hasMerchandiseInUpsell || !getAreSlotsAvailable();

    return {
        hideComponent:
            disablePayment ||
            // Incase, it is a bonus offering, we do now allow multiple user booking
            // as bonus offering is always user specific
            communityUuid ||
            !getIsTicketingEnabled({ event, eventData, isRebooking }) ||
            getIsInCartFlow({
                userFlow,
                hostData,
                disablePayment,
                dynamicLink,
                isRebooking,
            }) ||
            getQueryParam(URL_SEARCH_PARAMS.DISABLE_MULTI_BOOKING),
        decreaseQty,
        increaseQty,
        quantityTitle,
        disableDecreaseQty,
        disableIncreaseQty,
    };
};

export default useTicketCount;
